import { CROWD_RULES } from '@shared/types';
import { apiHeaders, apiKey, endpoint } from './config';

/**
 * Sending corrections to the shared ranking.
 *
 * What leaves the browser is a romanized word and the Devanagari word chosen for
 * it — `{ pani → पनि }`. Never the sentence it was in, never the surrounding
 * text, never an account, and — since the request is sent with `no-referrer` —
 * not the address of the page either. That is the whole of it, and it is the
 * reason this can run on a draft: a table of word choices is not a copy of
 * anyone's poem.
 *
 * There is no identifier of any kind in the payload. There used to be a random
 * per-browser id, which was both a thing stored on somebody's device for our
 * convenience and the forgeable half of the vote count; the server now decides
 * whose vote this is, from the connection.
 *
 * Contribution is on by default and switchable off (profile settings for a
 * writer, the tool itself for everyone else). The server honours the writer's
 * setting as well, so turning it off does not depend on this file behaving.
 */

const OPT_OUT_KEY = 'kaha:tl-contribute';

/** Buffered, then sent in one go — a correction is not worth a request. */
const FLUSH_AFTER_MS = 30_000;

interface Pick {
  roman: string;
  text: string;
}

let queue = new Map<string, Pick>();
let timer: ReturnType<typeof setTimeout> | null = null;
let listening = false;

/**
 * Whether a visitor's browser has asked not to be collected from.
 *
 * Global Privacy Control is a real, legally recognised signal in several
 * jurisdictions, and Do Not Track is the older gesture at the same thing.
 * Honouring them costs one line and is the difference between "collection is on
 * by default" being a defensible product decision and being one that ignores
 * people who have already said no in the only way a website lets them.
 *
 * It matters most in embeds, where the person typing has no relationship with us
 * at all and no settings page to find.
 */
function privacySignalSet(): boolean {
  const nav = navigator as Navigator & { globalPrivacyControl?: boolean; doNotTrack?: string };
  return nav.globalPrivacyControl === true || nav.doNotTrack === '1';
}

/** True unless this browser has opted out, or asked not to be tracked at all. */
export function contributesPicks(): boolean {
  if (privacySignalSet()) return false;
  try {
    return localStorage.getItem(OPT_OUT_KEY) !== 'off';
  } catch {
    return false;
  }
}

export function setContributesPicks(enabled: boolean): void {
  try {
    localStorage.setItem(OPT_OUT_KEY, enabled ? 'on' : 'off');
  } catch {
    /* Nothing to persist to; the choice lasts for this page. */
  }
  if (!enabled) queue = new Map();
}

/**
 * Sends what has been queued.
 *
 * `sendBeacon` first: the moment worth flushing at is the one where the page is
 * going away, and a normal fetch is cancelled with it. Falls back to a keepalive
 * fetch where beacons are unavailable.
 */
function flush(): void {
  if (timer) {
    clearTimeout(timer);
    timer = null;
  }

  const picks = [...queue.values()].slice(0, CROWD_RULES.maxBatch);
  if (!picks.length) return;

  queue = new Map();
  const body = JSON.stringify({ picks });

  /*
   * A beacon cannot carry a header, so an embed — which has to identify itself
   * with a key — takes the fetch path instead. `keepalive` is what makes that
   * survive the page going away, which is the whole reason a beacon was first
   * choice.
   *
   * A beacon also cannot set a referrer policy, so it is only used where the
   * page address is ours already.
   */
  if (!apiKey()) {
    try {
      if (navigator.sendBeacon?.(endpoint('/api/picks'), new Blob([body], { type: 'application/json' }))) {
        return;
      }
    } catch {
      /* Fall through to fetch. */
    }
  }

  void fetch(endpoint('/api/picks'), {
    method: 'POST',
    headers: apiHeaders({ 'Content-Type': 'application/json' }),
    body,
    keepalive: true,
    /*
     * Never tell the server which page this came from.
     *
     * A `Referer` header is attached by default, and on an embed that address is
     * the one piece of context that could turn an anonymous word into "somebody
     * on this page typed this". Nothing here needs it, so nothing here sends it.
     */
    referrerPolicy: 'no-referrer',
  }).catch(() => {
    /* A lost contribution is a lost contribution; nothing here is worth a retry
       queue, and the next correction will carry the same information. */
  });
}

function listen(): void {
  if (listening || typeof document === 'undefined') return;
  listening = true;

  // `visibilitychange` rather than `unload`: the latter is unreliable on mobile,
  // where a tab is far more likely to be backgrounded than closed.
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') flush();
  });
  window.addEventListener('pagehide', flush);
}

/** Queues one correction. Cheap, synchronous, and safe to call per keystroke. */
export function contributePick(roman: string, text: string): void {
  if (!contributesPicks()) return;

  queue.set(`${roman} ${text}`, { roman, text });
  listen();

  if (queue.size >= CROWD_RULES.maxBatch) {
    flush();
    return;
  }

  timer ??= setTimeout(flush, FLUSH_AFTER_MS);
}
